import { extractRankedCarriers, parseRateShoppingResponse } from "../Shipment/TabContent/ViewShipment/rateShoppingUtils";
import { collectCarrierScoringBreakdown } from "../../../utils/scoringExplanationCache";
import { isLaneScopedBaseKpi } from "../Analytics/KpiPlatform/kpiCatalogUtils";
import { scoringSpecKpiKey } from "./parityConfigMappers";
import { DEFAULT_ALLOCATION_WEIGHTS, KPI_LIBRARY } from "./strategyConstants";

const formatLogTime = (date = new Date()) =>
  date.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  });

/** Human-readable origin/destination label from config geo hierarchy fields. */
export function formatGeoEndpointFromConfig(cfg, side) {
  if (!cfg) return "";
  const isO = side === "origin";
  const h = isO ? cfg.originGeoHierarchy : cfg.destinationGeoHierarchy;
  if (!h || h === "Any Location") return "";

  if (h === "Location") {
    return (isO ? cfg.originGeoValue : cfg.destinationGeoValue || "").trim();
  }
  if (h === "City-State") {
    const city = (isO ? cfg.originCity : cfg.destCity || "").trim();
    const state = (isO ? cfg.originState : cfg.destState || "").trim();
    const country = (isO ? cfg.originCountry : cfg.destCountry || "").trim();
    return [city, state, country].filter(Boolean).join(", ");
  }
  if (h === "ZIP") {
    const zip = (isO ? cfg.originPostalCode : cfg.destPostalCode || "").trim();
    const state = (isO ? cfg.originState : cfg.destState || "").trim();
    const country = (isO ? cfg.originCountry : cfg.destCountry || "").trim();
    return [zip, state, country].filter(Boolean).join(", ");
  }
  if (h === "Country") {
    return (isO ? cfg.originGeoValue : cfg.destinationGeoValue || "").trim();
  }
  return "";
}

/** Today's date in YYYY-MM-DD for lane-paths operationDate. */
export function getTodayOperationDate() {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/** Empty stop shape aligned with shipment stop fields used for lane simulation. */
export const EMPTY_SIMULATION_STOP = Object.freeze({
  stopName: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  state: "",
  zipCode: "",
  country: "",
  locationERPIdentifier: "",
});

/** Map a location catalogue row into shipment-style stop fields. */
export function parseCombinedLocationAddress(value) {
  const parts = String(value || "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length < 3) {
    return { addressLine1: String(value || "").trim() };
  }

  if (parts.length >= 5) {
    return {
      addressLine1: parts.slice(0, parts.length - 4).join(", ") || parts[0],
      city: parts[parts.length - 4],
      state: parts[parts.length - 3],
      zipCode: parts[parts.length - 2],
      country: parts[parts.length - 1],
    };
  }

  if (parts.length === 4) {
    const last = parts[3];
    const looksLikeZip = /^[\dA-Z -]{3,12}$/i.test(last) && /\d/.test(last);
    return {
      addressLine1: parts[0],
      city: parts[1],
      state: parts[2],
      zipCode: looksLikeZip ? last : "",
      country: looksLikeZip ? "" : last,
    };
  }

  return {
    addressLine1: parts[0],
    city: parts[1],
    state: parts[2],
  };
}

export function locationRowToSimulationStop(row) {
  if (!row || typeof row !== "object") {
    return { ...EMPTY_SIMULATION_STOP };
  }

  const combinedAddress = (row.address || "").trim();
  let addressLine1 = (row.addressLine1 || "").trim();
  let addressLine2 = (row.addressLine2 || "").trim();
  let city = (row.city || row.cityName || "").trim();
  let state = (row.state || row.stateCode || row.stateName || "").trim();
  let zipCode = (row.zipCode || row.postalCode || row.zip || "").trim();
  let country = (row.country || row.countryCode || row.countryName || "").trim();

  const shouldParseCombined =
    combinedAddress &&
    (!city || !state || !zipCode || addressLine1 === combinedAddress || !addressLine1);

  if (shouldParseCombined) {
    const parsed = parseCombinedLocationAddress(combinedAddress);
    addressLine1 = addressLine1 && addressLine1 !== combinedAddress ? addressLine1 : parsed.addressLine1 || "";
    addressLine2 = addressLine2 || parsed.addressLine2 || "";
    city = city || parsed.city || "";
    state = state || parsed.state || "";
    zipCode = zipCode || parsed.zipCode || "";
    country = country || parsed.country || "";
  }

  if (!addressLine1 && combinedAddress && !shouldParseCombined) {
    addressLine1 = combinedAddress;
  }

  return {
    stopName: (row.locationName || row.name || "").trim(),
    addressLine1,
    addressLine2,
    city,
    state,
    zipCode,
    country,
    locationERPIdentifier: String(
      row.locationRef || row.customLocationIdentifier || row.id || row.locationId || ""
    ).trim(),
  };
}

/** Match shipment rate-shopping address formatting for lane-paths. */
export function formatSimulationStopAddress(stop) {
  return `${stop?.addressLine1 || ""}${stop?.addressLine2 || ""}, ${stop?.city || ""}, ${
    stop?.state || ""
  }, ${stop?.zipCode || ""}, ${stop?.country || ""}`;
}

export function isSimulationStopValid(stop) {
  if (!stop || typeof stop !== "object") return false;
  // A location name or address alone isn't enough to geocode reliably - state/zip/country
  // must all be present too, or simulation can silently price off an incomplete address
  // match (e.g. a too-broad city-level graph node) instead of erroring (LGX: $98 price
  // for a lane with no zip code).
  const hasLocationIdentifier = Boolean(
    (stop.stopName || "").trim() ||
      (stop.city || "").trim() ||
      (stop.addressLine1 || "").trim()
  );
  const state = (stop.state || "").trim();
  const zipCode = (stop.zipCode || "").trim();
  const country = (stop.country || "").trim();
  return hasLocationIdentifier && Boolean(state) && Boolean(zipCode) && Boolean(country);
}

/** Human-readable lane label for toasts and logs. */
export function simulationStopLabel(stop) {
  return (stop?.stopName || stop?.city || stop?.addressLine1 || stop?.zipCode || "").trim();
}

/** One endpoint segment of backend carrier_kpi.lane_key (city|state|zip). */
function simulationLaneKeySegment(stop) {
  if (!stop || typeof stop !== "object") return "";
  const city = String(stop.city || "").trim();
  const state = String(stop.state || "").trim();
  const zip = String(stop.zipCode ?? stop.zip ?? stop.postalCode ?? "").trim();
  if (!city && !state && !zip) return "";
  return [city, state, zip].join("|");
}

/** Corridor key aligned with backend ShipmentLaneKeyService (city|state|zip->city|state|zip). */
export function buildSimulationLaneKey(originStop = {}, destinationStop = {}) {
  const origin = simulationLaneKeySegment(originStop);
  const destination = simulationLaneKeySegment(destinationStop);
  if (!origin || !destination) return "";
  return `${origin}->${destination}`;
}

const normalizeSimulationLaneKeySegment = (segment = "") => {
  const parts = String(segment || "")
    .split("|")
    .map((part) => part.trim());
  if (!parts.length) return "";
  const city = (parts[0] || "").toLowerCase();
  const state = (parts[1] || "").toUpperCase();
  const zipRaw = parts[2] || "";
  const zipDigits = zipRaw.replace(/\D/g, "");
  const zip = (zipDigits.length >= 5 ? zipDigits.slice(0, 5) : zipRaw).trim();
  return [city, state, zip].filter(Boolean).join("|");
};

/** Normalize lane_key for comparison (case, zip width, arrow spacing). */
export function normalizeSimulationLaneKey(laneKey) {
  const raw = String(laneKey ?? "").trim();
  if (!raw) return "";
  const arrow = raw.includes("->") ? "->" : raw.includes("→") ? "→" : "->";
  const parts = raw.split(/->|→/).map((part) => normalizeSimulationLaneKeySegment(part.trim()));
  if (parts.length < 2 || !parts[0] || !parts[1]) return raw.toLowerCase();
  return `${parts[0]}->${parts[1]}`;
}

const laneKeysEqual = (a, b) => {
  const left = normalizeSimulationLaneKey(a);
  const right = normalizeSimulationLaneKey(b);
  if (!left || !right) return false;
  return left === right;
};

/**
 * Graph {@code origin}/{@code destination} query params for lane-paths.
 * Only a catalogue location name (typeahead / config Location geo) — never city alone.
 * When empty, the backend uses {@code originAddress}/{@code destinationAddress} for graph lookup
 * (same idea as shipment rate shopping on full addresses).
 */
export function simulationLanePathLocationParam(stop) {
  return (stop?.stopName || "").trim();
}

/** Pre-fill a simulation stop from config geo routing fields. */
export function simulationStopFromConfig(cfg, side) {
  const stop = { ...EMPTY_SIMULATION_STOP };
  if (!cfg) return stop;

  const isO = side === "origin";
  const h = isO ? cfg.originGeoHierarchy : cfg.destinationGeoHierarchy;
  if (!h || h === "Any Location") return stop;

  if (h === "Location") {
    stop.stopName = (isO ? cfg.originGeoValue : cfg.destinationGeoValue || "").trim();
    stop.locationERPIdentifier = (isO ? cfg.originLocationId : cfg.destLocationId || "").trim();
    return stop;
  }
  if (h === "City-State") {
    stop.city = (isO ? cfg.originCity : cfg.destCity || "").trim();
    stop.state = (isO ? cfg.originState : cfg.destState || "").trim();
    stop.country = (isO ? cfg.originCountry : cfg.destCountry || "").trim();
    return stop;
  }
  if (h === "ZIP") {
    stop.zipCode = (isO ? cfg.originPostalCode : cfg.destPostalCode || "").trim();
    stop.state = (isO ? cfg.originState : cfg.destState || "").trim();
    stop.country = (isO ? cfg.originCountry : cfg.destCountry || "").trim();
    return stop;
  }
  if (h === "Country") {
    stop.country = (isO ? cfg.originGeoValue : cfg.destinationGeoValue || "").trim();
  }
  return stop;
}

/** Location search payload for simulation origin/destination lookup. */
export function buildLocationLookupPayloadByName(locationName) {
  const v = (locationName || "").trim();
  if (!v) return null;
  return { locationName: v };
}

/** Normalize location search API payloads into a row array. */
export function extractLocationSearchResults(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.content)) return data.content;
  if (Array.isArray(data?.locations)) return data.locations;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.data)) return data.data;
  if (data && typeof data === "object") {
    if (data.locationName || data.name || data.locationRef || data.id || data.locationId) {
      return [data];
    }
  }
  return [];
}

/** Filter catalog rows to the active originator or accessible offices. */
export function filterLocationRowsByOfficeScope(
  rows = [],
  { officeCode, scope, accessibleOfficeCodes } = {}
) {
  if (scope === "all-accessible") {
    return Array.isArray(rows) ? rows : [];
  }
  return (Array.isArray(rows) ? rows : []).filter((row) =>
    locationRowMatchesOfficeScope(row, { officeCode, scope, accessibleOfficeCodes })
  );
}

const readLocationSearchOfficeCode = (row) =>
  String(row?.officeCode || row?.office_code || "")
    .trim()
    .toUpperCase();

/** Keep rows with no office code (search API) or rows within the active office scope. */
const locationRowMatchesOfficeScope = (
  row,
  { officeCode, scope, accessibleOfficeCodes } = {}
) => {
  const rowOffice = readLocationSearchOfficeCode(row);
  if (!rowOffice) return true;

  if (scope === "originator") {
    const code = String(officeCode || "")
      .trim()
      .toUpperCase();
    return !code || rowOffice === code;
  }

  if (scope === "all-accessible") {
    const allowed = new Set(
      (Array.isArray(accessibleOfficeCodes) ? accessibleOfficeCodes : [])
        .map((code) => String(code || "").trim().toUpperCase())
        .filter(Boolean)
    );
    return !allowed.size || allowed.has(rowOffice);
  }

  return true;
};

/** Pick the best location row for simulation lookup, scoped by office access. */
export function pickSimulationLocationSearchResult(
  data,
  { officeCode, scope, accessibleOfficeCodes } = {}
) {
  const list = extractLocationSearchResults(data).filter((row) =>
    locationRowMatchesOfficeScope(row, { officeCode, scope, accessibleOfficeCodes })
  );
  if (!list.length) return null;

  if (scope === "originator") {
    const code = String(officeCode || "")
      .trim()
      .toUpperCase();
    if (code) {
      return list.find((row) => readLocationSearchOfficeCode(row) === code) || list[0];
    }
  }

  return list[0];
}

/** Filter and normalize location search results for simulation typeahead dropdowns. */
export function filterSimulationLocationSearchResults(
  data,
  { officeCode, scope, accessibleOfficeCodes, limit = 8 } = {}
) {
  let filtered = extractLocationSearchResults(data);
  if (scope !== "all-accessible") {
    filtered = filtered.filter((row) =>
      locationRowMatchesOfficeScope(row, { officeCode, scope, accessibleOfficeCodes })
    );
  }

  return dedupeLocationSearchOptions(
    filtered.map(normalizeLocationSearchOption).filter(Boolean)
  )
    .sort((a, b) => a.locationName.localeCompare(b.locationName))
    .slice(0, limit);
}

/** Normalize location text for fuzzy typeahead matching (ignore #, punctuation, extra spaces). */
export function normalizeLocationSearchText(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[#_,./\\-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function locationMatchesSearchQuery(option, query) {
  const normalizedQuery = normalizeLocationSearchText(query);
  if (!normalizedQuery) return true;

  const haystack = normalizeLocationSearchText(
    [
      option?.locationName,
      option?.city,
      option?.state,
      option?.locationId,
      option?.officeCode,
      option?.customLocationIdentifier,
      option?.addressLine1,
      option?.site,
      option?.parentName,
    ]
      .filter(Boolean)
      .join(" ")
  );

  if (haystack.includes(normalizedQuery)) return true;

  const tokens = normalizedQuery.split(" ").filter(Boolean);
  return tokens.length > 0 && tokens.every((token) => haystack.includes(token));
}

/** Normalize a location row from list/search APIs for simulation typeahead. */
export function normalizeLocationSearchOption(row) {
  if (!row || typeof row !== "object") return null;
  const locationName = (row.locationName || row.name || "").trim();
  if (!locationName) return null;
  return {
    locationId: String(row.locationRef || row.id || row.locationId || "").trim(),
    locationName,
    city: (row.city || "").trim(),
    state: (row.state || row.stateCode || "").trim(),
    officeCode: (row.officeCode || row.office_code || "").trim(),
    customLocationIdentifier: String(
      row.customLocationIdentifier || row.customLocationERP || row.locationERPIdentifier || ""
    ).trim(),
    addressLine1: (row.addressLine1 || row.address || "").trim(),
    site: (row.site || "").trim(),
    parentName: (row.parentName || "").trim(),
    rawRow: row,
  };
}

/** Stable key for deduping typeahead rows that share the same visible label. */
export function locationSearchDedupeKey(option) {
  if (!option) return "";
  const nameKey = normalizeLocationSearchText(option.locationName);
  if (nameKey) return `name:${nameKey}`;
  const id = String(option.locationId || "").trim();
  if (id) return `id:${id}`;
  return `office:${option.officeCode || ""}:${String(option.locationName || "").toLowerCase()}`;
}

/** Dedupe by display name — search/grouped APIs often return the same location with different ids. */
export function dedupeLocationSearchOptions(options = []) {
  const byKey = new Map();

  for (const option of Array.isArray(options) ? options : []) {
    if (!option) continue;
    const key = locationSearchDedupeKey(option);
    if (!key) continue;

    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, option);
      continue;
    }
    if (!existing.locationId && option.locationId) {
      byKey.set(key, option);
    }
  }

  return [...byKey.values()];
}

/** Filter location options for typeahead (name, city, state, id, office). */
export function filterLocationSearchOptions(options = [], query = "", limit = 20) {
  const list = Array.isArray(options) ? options : [];
  const q = (query || "").trim().toLowerCase();
  const filtered = !q
    ? list
    : list.filter((loc) => {
        const fields = [
          loc.locationName,
          loc.parentName,
          loc.site,
          loc.addressLine1,
          loc.city,
          loc.state,
          loc.locationId,
          loc.officeCode,
          loc.customLocationIdentifier,
        ];
        const plain = fields.filter(Boolean).join(" ").toLowerCase();
        if (plain.includes(q)) return true;
        return locationMatchesSearchQuery(loc, q);
      });

  return dedupeLocationSearchOptions(filtered).slice(0, limit);
}

const registerCarrierName = (lookup, code, name) => {
  const normalizedCode = String(code || "").trim().toUpperCase();
  const normalizedName = String(name || "").trim();
  if (!normalizedCode || !normalizedName) return;
  if (normalizedName.toUpperCase() === normalizedCode) return;
  if (!lookup.has(normalizedCode)) {
    lookup.set(normalizedCode, normalizedName);
  }
};

/** Build carrier code → display name map from lane-paths / all-paths route solutions. */
export function buildCarrierNameLookupFromRouteSolutions(data) {
  const lookup = new Map();
  const parsed = parseRateShoppingResponse(data);

  for (const path of parsed.paths || []) {
    const routeNames = path.routeCarrierNames || [];
    (path.routeCarrierCodes || []).forEach((code, index) => {
      registerCarrierName(lookup, code, routeNames[index] || routeNames[0]);
    });
    (path.rateCarrierCodes || []).forEach((code) => {
      registerCarrierName(lookup, code, routeNames[0]);
    });

    for (const segment of path.pathSegments || []) {
      registerCarrierName(
        lookup,
        segment.routeCarrierCode ?? segment.route_carrier_code,
        segment.routeCarrierName ?? segment.route_carrier_name
      );
      const breakdown = segment.routeRate?.rateBreakdown || segment.routeRate?.rate_breakdown || [];
      for (const item of breakdown) {
        registerCarrierName(
          lookup,
          item.rateCarrierCode ?? item.rate_carrier_code,
          item.rateCarrierName ?? item.rate_carrier_name
        );
      }
    }
  }

  return lookup;
}

/** Build carrier code → organization name map from GET /api/organizations/get-carriers. */
export function buildCarrierNameLookupFromOrganizations(organizations = []) {
  const lookup = new Map();
  for (const org of organizations || []) {
    registerCarrierName(
      lookup,
      org.officeCode ?? org.office_code ?? org.code,
      org.name ?? org.organizationName ?? org.displayName
    );
  }
  return lookup;
};

export const isSimulationCarrierNameMissing = (row) => {
  const code = String(row?.carrierCode || "").trim().toUpperCase();
  const name = String(row?.carrierName || "").trim();
  return !name || name.toUpperCase() === code;
};

export function applyCarrierNamesToRankedRows(rankedRows = [], nameByCode) {
  const lookup = nameByCode instanceof Map ? nameByCode : new Map(nameByCode || []);
  if (!lookup.size) return rankedRows;

  return rankedRows.map((row) => {
    if (!isSimulationCarrierNameMissing(row)) return row;
    const code = String(row.carrierCode || "").trim().toUpperCase();
    const resolvedName = lookup.get(code);
    if (!resolvedName) return row;
    return { ...row, carrierName: resolvedName };
  });
}

export const extractSimulationRankedCarriers = (data) => {
  let rawRows = extractRankedCarriers(data);

  if (!rawRows.length) {
    const parsed = parseRateShoppingResponse(data);
    rawRows = parsed.rankedCarriers || [];
  }

  if (!rawRows.length) {
    const nested =
      data?.selection_outcome?.ranked_carriers ||
      data?.selectionOutcome?.rankedCarriers ||
      data?.result?.ranked_carriers ||
      data?.result?.rankedCarriers;
    if (Array.isArray(nested) && nested.length) {
      rawRows = nested;
    }
  }

  const normalized = normalizeRankedCarrierRows(rawRows);
  const nameLookup = buildCarrierNameLookupFromRouteSolutions(data);
  return applyCarrierNamesToRankedRows(normalized, nameLookup);
};

const KPI_KEY_LABELS = {
  on_time: "On-Time Performance",
  on_time_performance: "On-Time Performance",
  on_time_delivery: "On-Time Delivery",
  on_time_pickup: "On-Time Pickup",
  acceptance: "Acceptance Rate",
  acceptance_rate: "Acceptance Rate",
  tender_acceptance_rate: "Tender Acceptance Rate",
  price: "Price Efficiency",
  price_efficiency: "Price Efficiency",
  cost_efficiency: "Cost Efficiency",
  claims_free: "Claims-Free Ratio",
  claims_free_ratio: "Claims-Free Ratio",
  capacity: "Capacity Match",
  capacity_match: "Capacity Match",
  transit: "Transit Time",
  transit_time: "Transit Time",
};

/** Map strategy / UI KPI keys to ranked-carrier metric keys from lane-paths. */
const KPI_KEY_ALIASES = {
  cost_efficiency: ["price_efficiency", "price", "base_cost", "base_cost_7d"],
  on_time_performance: [
    "on_time",
    "on_time_delivery",
    "on_time_pickup",
    "on_time_pickup_7d",
    "on_time_pickup_90d",
    "on_time_delivery_90d",
    "otp",
    "otd",
  ],
  tender_acceptance_rate: [
    "acceptance_rate",
    "acceptance",
    "acceptance_rate_30d",
    "tender_acceptance_rate_7d",
    "tender_acceptance_rate_30d",
  ],
  claims_free_ratio: [
    "claims_free",
    "claims_ratio",
    "claims_free_ratio_30d",
  ],
  lane_level_claims_free_ratio: ["claims_free_ratio", "claims_free", "claims_ratio"],
  invoice_rejected: ["billing_accuracy", "billing_accuracy_rate"],
  billing_accuracy: ["invoice_rejected", "billing_accuracy_rate"],
  price_efficiency: ["cost_efficiency", "price", "base_cost", "base_cost_7d"],
  acceptance_rate: ["tender_acceptance_rate", "acceptance", "tender_acceptance_rate_30d"],
  base_cost: ["base_cost_7d", "cost_efficiency", "price_efficiency"],
  base_cost_7d: ["base_cost", "cost_efficiency", "price_efficiency"],
  on_time_pickup: ["otp", "on_time_performance"],
  on_time_delivery: ["otd", "on_time_performance"],
};

/** Legacy carrier_kpi.kpi_name values → canonical KPI keys (mirrors backend resolver). */
const CARRIER_KPI_LEGACY_NAMES = {
  otp: "on_time_pickup",
  otd: "on_time_delivery",
  claims_ratio: "claims_free_ratio",
  acceptance_rate: "tender_acceptance_rate",
  acceptance_rate_30d: "tender_acceptance_rate_30d",
  claims_free_ratio_30d: "claims_free_ratio",
};

const toNumber = (value) => {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

export const normalizeKpiKey = (key) =>
  String(key || "")
    .trim()
    .toLowerCase()
    .replace(/[^\w]+/g, "_")
    .replace(/^_+|_+$/g, "");

const normalizeCarrierKpiName = (name) => {
  const key = normalizeKpiKey(name);
  return CARRIER_KPI_LEGACY_NAMES[key] || key;
};

export const labelForSimulationKpiKey = (key) => {
  const normalized = normalizeKpiKey(key);
  return KPI_KEY_LABELS[normalized] || normalized.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

const isLaneLevelSimulationKpiKey = (key) =>
  normalizeKpiKey(key).startsWith("lane_level_");

/** Aliases for display / carrier_kpi row lookup (may include catalog variants). */
export const expandSimulationKpiKeyAliases = (key) => {
  const normalized = normalizeKpiKey(key);
  if (!normalized) return [];
  const aliases = new Set([normalized]);

  const withoutWindow = normalized.replace(/_\d+d$/i, "");
  if (withoutWindow && withoutWindow !== normalized) {
    aliases.add(withoutWindow);
  }

  (KPI_KEY_ALIASES[normalized] || []).forEach((alias) => aliases.add(normalizeKpiKey(alias)));
  for (const [canonical, list] of Object.entries(KPI_KEY_ALIASES)) {
    if (list.some((alias) => normalizeKpiKey(alias) === normalized)) {
      aliases.add(canonical);
      list.forEach((alias) => aliases.add(normalizeKpiKey(alias)));
    }
  }
  return [...aliases];
};

/** Aliases for KPI-factor penalty lookup — never mix office-scoped and lane-scoped keys. */
export const expandSimulationPenaltyLookupAliases = (key) => {
  const normalized = normalizeKpiKey(key);
  if (!normalized) return [];
  const laneScoped = isLaneLevelSimulationKpiKey(normalized);
  const aliases = new Set([normalized]);

  const withoutWindow = normalized.replace(/_\d+d$/i, "");
  if (withoutWindow && withoutWindow !== normalized) {
    aliases.add(withoutWindow);
  }

  const registerAlias = (raw) => {
    const alias = normalizeKpiKey(raw);
    if (!alias) return;
    if (laneScoped !== isLaneLevelSimulationKpiKey(alias)) return;
    aliases.add(alias);
  };

  (KPI_KEY_ALIASES[normalized] || []).forEach(registerAlias);
  for (const [canonical, list] of Object.entries(KPI_KEY_ALIASES)) {
    if (laneScoped !== isLaneLevelSimulationKpiKey(canonical)) continue;
    if (list.some((alias) => normalizeKpiKey(alias) === normalized) || canonical === normalized) {
      aliases.add(normalizeKpiKey(canonical));
      list.forEach(registerAlias);
    }
  }

  if (laneScoped) {
    const legacyBase = normalized.replace(/(_per_office)?_\d+d$/i, "");
    if (legacyBase && legacyBase !== normalized) aliases.add(legacyBase);
  }

  return [...aliases];
};

const isCostStrategyKpiKey = (key) =>
  expandSimulationKpiKeyAliases(key).some(
    (alias) =>
      alias === "cost_efficiency" ||
      alias === "price_efficiency" ||
      alias === "price" ||
      alias.startsWith("base_cost")
  );

export function mergeSimulationKpiScores(...groups) {
  const merged = new Map();
  for (const group of groups) {
    for (const score of group || []) {
      if (!score?.key || score.value == null || score.value === "") continue;
      const key = normalizeKpiKey(score.key);
      if (merged.has(key)) continue;
      merged.set(key, {
        key,
        label: score.label || labelForSimulationKpiKey(key),
        value: score.value,
        ...(score.penaltyMultiplier != null ? { penaltyMultiplier: score.penaltyMultiplier } : {}),
      });
    }
  }
  return [...merged.values()];
}

const PENALTY_MULTIPLIER_MAX = 2;

/** Mirrors backend {@code CarrierKpiMultiplier.toOnTimeRate}. */
export function penaltyMultiplierToOnTimeRate(storedValue) {
  const n = toNumber(storedValue);
  if (n == null || n <= 0) return null;
  if (n <= 1) return Math.min(1, n);
  const rate = PENALTY_MULTIPLIER_MAX - n;
  return Math.max(0, Math.min(1, rate));
}

const pushKpiScore = (scores, seen, { key, label, value, penaltyMultiplier }) => {
  const normalizedKey = normalizeKpiKey(key);
  if (!normalizedKey || normalizedKey === "cost" || normalizedKey === "sample_size") return;
  if (value == null || value === "" || seen.has(normalizedKey)) return;
  seen.add(normalizedKey);
  scores.push({
    key: normalizedKey,
    label: label || labelForSimulationKpiKey(normalizedKey),
    value: toNumber(value) ?? value,
    ...(penaltyMultiplier != null && penaltyMultiplier !== ""
      ? { penaltyMultiplier: toNumber(penaltyMultiplier) ?? penaltyMultiplier }
      : {}),
  });
};

/** Pull individual KPI metric values from a ranked carrier API row. */
export function extractKpiScoresFromRankedCarrier(row) {
  if (!row || typeof row !== "object") return [];

  const scores = [];
  const seen = new Set();

  if (Array.isArray(row.kpis)) {
    row.kpis.forEach((entry) => {
      const key = entry?.metric_name ?? entry?.metricName ?? entry?.name;
      const metricKey = normalizeKpiKey(key);
      const isCost = metricKey === "cost";
      const penaltyMultiplier = isCost
        ? null
        : toNumber(entry?.value ?? entry?.score ?? entry?.penalty_multiplier ?? entry?.penaltyMultiplier);
      const onTimeRate = isCost
        ? null
        : toNumber(entry?.on_time_rate ?? entry?.onTimeRate);
      pushKpiScore(scores, seen, {
        key,
        label: labelForSimulationKpiKey(key),
        value: isCost
          ? entry?.value ?? entry?.score
          : onTimeRate ??
            (penaltyMultiplier != null
              ? penaltyMultiplierToOnTimeRate(penaltyMultiplier)
              : null),
        penaltyMultiplier,
      });
    });
  }

  const kpiScores = row.kpi_scores ?? row.kpiScores;
  if (Array.isArray(kpiScores)) {
    kpiScores.forEach((entry) => {
      pushKpiScore(scores, seen, {
        key: entry?.kpi ?? entry?.name ?? entry?.metric_name ?? entry?.metricName,
        label: entry?.label ?? entry?.name,
        value: entry?.score ?? entry?.value ?? entry?.penalty_multiplier ?? entry?.penaltyMultiplier,
      });
    });
  } else if (kpiScores && typeof kpiScores === "object") {
    Object.entries(kpiScores).forEach(([key, value]) => {
      pushKpiScore(scores, seen, { key, value });
    });
  }

  const steps = row.kpi_factor_detail?.steps ?? row.kpiFactorDetail?.steps;
  if (Array.isArray(steps)) {
    steps.forEach((step) => {
      pushKpiScore(scores, seen, {
        key: step?.kpi_name ?? step?.kpiName,
        label: labelForSimulationKpiKey(step?.kpi_name ?? step?.kpiName),
        value:
          step?.on_time_rate ??
          step?.onTimeRate ??
          step?.raw_score ??
          step?.rawScore ??
          step?.penalty_multiplier ??
          step?.penaltyMultiplier ??
          step?.score,
      });
    });
  }

  const detail = row.kpi_factor_detail ?? row.kpiFactorDetail;
  const penaltyMap = detail?.penalty_multipliers ?? detail?.penaltyMultipliers;
  if (penaltyMap && typeof penaltyMap === "object" && !Array.isArray(penaltyMap)) {
    Object.entries(penaltyMap).forEach(([key, value]) => {
      pushKpiScore(scores, seen, { key, value });
    });
  }

  if (row.metrics && typeof row.metrics === "object" && !Array.isArray(row.metrics)) {
    Object.entries(row.metrics).forEach(([key, value]) => {
      pushKpiScore(scores, seen, { key, value });
    });
  }

  return scores;
}

export const strategyKpiToKey = (kpi) =>
  normalizeKpiKey(
    kpi?.scoringWeightKey ??
      kpi?.scoring_weight_key ??
      kpi?.scoringKey ??
      kpi?.metric_name ??
      kpi?.metricName ??
      kpi?.id ??
      kpi?.name
  );

/** Build dynamic KPI columns from strategy config and ranked carrier rows. */
export function buildSimulationKpiColumns(rankedRows = [], strategyKpis = []) {
  const columns = [];
  const seen = new Set();

  (Array.isArray(strategyKpis) ? strategyKpis : []).forEach((kpi) => {
    const key = strategyKpiToKey(kpi);
    if (!key || seen.has(key)) return;
    seen.add(key);
    columns.push({
      key,
      label: (kpi?.name || labelForSimulationKpiKey(key)).trim(),
    });
  });

  rankedRows.forEach((row) => {
    (row?.kpiScores || []).forEach((score) => {
      if (!score?.key || seen.has(score.key)) return;
      seen.add(score.key);
      columns.push({
        key: score.key,
        label: score.label || labelForSimulationKpiKey(score.key),
      });
    });
  });

  return columns;
}

const findSimulationKpiScore = (row, kpiKey) => {
  const exact = normalizeKpiKey(kpiKey);
  if (!exact) return null;

  for (const score of row?.kpiScores || []) {
    if (normalizeKpiKey(score.key) === exact) return score;
  }

  const wanted = new Set(expandSimulationPenaltyLookupAliases(kpiKey));
  for (const score of row?.kpiScores || []) {
    const scoreKey = normalizeKpiKey(score.key);
    if (wanted.has(scoreKey)) return score;
  }
  return null;
};

const findSourceRowKpiMetric = (row, kpiKey) => {
  const kpis = row?.sourceRow?.kpis;
  if (!Array.isArray(kpis) || !kpis.length) return null;
  const exact = normalizeKpiKey(kpiKey);
  const aliases = new Set(expandSimulationPenaltyLookupAliases(kpiKey));

  for (const entry of kpis) {
    const name = normalizeKpiKey(entry?.metric_name ?? entry?.metricName ?? entry?.name);
    if (name && name === exact) return entry;
  }
  for (const entry of kpis) {
    const name = normalizeKpiKey(entry?.metric_name ?? entry?.metricName ?? entry?.name);
    if (name && aliases.has(name)) return entry;
  }
  return null;
};

const simulationPerformanceRateFromPenalty = (penalty) => {
  const p = toNumber(penalty);
  if (p == null || p <= 0) return null;
  if (p <= 1) return Math.min(1, p);
  return Math.min(1, 1 / p);
};

/** Performance score (0–1 rate) for a strategy KPI column — not the penalty multiplier. */
export const getSimulationKpiPerformanceScore = (row, kpiKey) => {
  const score = findSimulationKpiScore(row, kpiKey);
  const enrichedRate = toNumber(score?.onTimeRate ?? score?.value);
  if (enrichedRate != null && enrichedRate <= 1) return enrichedRate;

  const fromApi = findSourceRowKpiMetric(row, kpiKey);
  if (fromApi) {
    const apiRate = toNumber(fromApi.on_time_rate ?? fromApi.onTimeRate);
    if (apiRate != null) return apiRate;
    return simulationPerformanceRateFromPenalty(
      fromApi.penalty_multiplier ?? fromApi.penaltyMultiplier ?? fromApi.value
    );
  }

  if (score) {
    if (enrichedRate != null && enrichedRate > 1) {
      return simulationPerformanceRateFromPenalty(enrichedRate);
    }
    return simulationPerformanceRateFromPenalty(score.penaltyMultiplier);
  }

  return null;
};

/** @deprecated Alias — use {@link getSimulationKpiPerformanceScore}. */
export const getSimulationKpiValue = (row, kpiKey) =>
  getSimulationKpiPerformanceScore(row, kpiKey);

/** Simulation ranked-table KPI columns (performance score). */
export const getSimulationKpiTableValue = (row, kpiKey) =>
  getSimulationKpiPerformanceScore(row, kpiKey);

const PENALTY_CARRIER_KPI_NAMES = new Set([
  "on_time_pickup",
  "otp",
  "otd",
  "on_time_delivery",
  "tender_acceptance_rate",
  "acceptance_rate",
  "claims_free_ratio",
  "claims_ratio",
  "billing_accuracy",
]);

const isCostCarrierKpiName = (name) => {
  const key = normalizeKpiKey(name);
  return key === "cost" || key.startsWith("base_cost");
};

/** Convert stored carrier_kpi penalty multipliers to 0–1 display rates. */
export function carrierKpiStoredValueToRate(storedValue, { isPenalty = true, kpiName = "" } = {}) {
  const n = toNumber(storedValue);
  if (n == null || n <= 0) return null;
  if (!isPenalty || isCostCarrierKpiName(kpiName)) return n;
  return penaltyMultiplierToOnTimeRate(n);
}

export function isPenaltyCarrierKpiName(name, catalogBridgedNames = new Set()) {
  const key = normalizeKpiKey(name);
  if (!key || isCostCarrierKpiName(key)) return false;
  if (key.startsWith("lane_level_")) return true;
  if (PENALTY_CARRIER_KPI_NAMES.has(key)) return true;
  const withoutLane = key.replace(/^lane_level_/, "");
  if (withoutLane !== key && PENALTY_CARRIER_KPI_NAMES.has(withoutLane)) return true;
  return catalogBridgedNames.has(key);
}

/** Map carrier_kpi.kpi_name values to scoring_spec weight keys from the KPI catalog. */
export function buildCarrierKpiNameToScoringKeyMap(kpiCatalog = []) {
  const map = new Map();

  const register = (rawName, scoringKey) => {
    const key = normalizeKpiKey(scoringKey);
    if (!key) return;
    const normalizedName = normalizeKpiKey(rawName);
    if (normalizedName) map.set(normalizedName, key);
    const trimmed = String(rawName || "").trim().toLowerCase();
    if (trimmed) map.set(trimmed, key);
  };

  for (const base of Array.isArray(kpiCatalog) ? kpiCatalog : []) {
    const parentKey = normalizeKpiKey(base.kpiCode);
    for (const variant of base.selectionVariants || []) {
      const scoringKey =
        variant.scoringWeightKey || scoringSpecKpiKey(variant.displayName || variant.variantCode);
      if (parentKey) register(parentKey, scoringKey);
      for (const candidate of [
        variant.carrierKpiName,
        variant.scoringWeightKey,
        variant.variantCode,
        variant.displayName,
        base.kpiCode,
        variant.parentKpiCode,
      ]) {
        register(candidate, scoringKey);
      }
    }
  }

  for (const row of KPI_LIBRARY) {
    register(row.name, scoringSpecKpiKey(row.name));
    register(row.id, scoringSpecKpiKey(row.name));
  }

  return map;
}

export function collectCatalogBridgedCarrierKpiNames(kpiCatalog = []) {
  const names = new Set();
  for (const base of Array.isArray(kpiCatalog) ? kpiCatalog : []) {
    for (const variant of base.selectionVariants || []) {
      for (const candidate of [variant.carrierKpiName, variant.variantCode]) {
        const key = normalizeKpiKey(candidate);
        if (key) names.add(key);
      }
    }
  }
  return names;
}

const pickPreferredCarrierKpiRow = (
  rows = [],
  { preferredOffice = "", preferredLaneKey = "", preferLaneScopedRow = false } = {}
) => {
  const list = Array.isArray(rows) ? rows : [];
  if (!list.length) return null;
  const office = String(preferredOffice || "").trim().toUpperCase();
  const lane = String(preferredLaneKey || "").trim();
  const sorted = [...list].sort((a, b) =>
    String(b.computedAt || "").localeCompare(String(a.computedAt || ""))
  );

  const filterByOffice = (candidates) => {
    if (!office) return candidates;
    const match = candidates.filter(
      (row) => String(row.officeCode || "").trim().toUpperCase() === office
    );
    return match.length ? match : candidates;
  };

  if (preferLaneScopedRow) {
    if (!lane) return null;
    const laneMatches = filterByOffice(
      sorted.filter((row) => laneKeysEqual(row.laneKey, lane))
    );
    return laneMatches[0] || null;
  }

  const officeWide = filterByOffice(
    sorted.filter((row) => !String(row.laneKey || "").trim())
  );
  return officeWide[0] || null;
};

function isStrategyKpiLaneScoped(kpi, kpiCatalog = []) {
  const displayName = String(kpi?.name || "").trim();
  if (/^lane\s/i.test(displayName)) return true;

  const strategyKey = strategyKpiToKey(kpi);
  if (!strategyKey) return false;
  if (isLaneLevelSimulationKpiKey(strategyKey)) return true;
  const strategyAliases = new Set(expandSimulationKpiKeyAliases(strategyKey));

  for (const base of Array.isArray(kpiCatalog) ? kpiCatalog : []) {
    if (!isLaneScopedBaseKpi(base)) continue;
    for (const variant of base.selectionVariants || []) {
      const variantKey = normalizeKpiKey(
        variant.scoringWeightKey ||
          scoringSpecKpiKey(variant.displayName || variant.variantCode)
      );
      const variantAliases = expandSimulationKpiKeyAliases(variantKey);
      if (variantAliases.some((alias) => strategyAliases.has(alias))) return true;
      if (normalizeKpiKey(kpi?.variantCode) === normalizeKpiKey(variant.variantCode)) {
        return true;
      }
    }
  }
  return false;
}

/** All carrier_kpi / catalog names that should satisfy a strategy KPI column lookup. */
function collectStrategyKpiLookupNames(kpi, kpiCatalog = []) {
  const strategyKey = strategyKpiToKey(kpi);
  const names = new Set();
  if (!strategyKey) return names;

  const register = (rawName) => {
    const normalized = normalizeKpiKey(rawName);
    if (!normalized) return;
    names.add(normalized);
    expandSimulationKpiKeyAliases(normalized).forEach((alias) => names.add(alias));
  };

  register(strategyKey);
  register(kpi?.name);
  register(kpi?.variantCode);
  register(kpi?.scoringWeightKey ?? kpi?.scoring_weight_key);
  register(scoringSpecKpiKey(kpi?.name));

  const strategyAliases = new Set(expandSimulationKpiKeyAliases(strategyKey));
  for (const base of Array.isArray(kpiCatalog) ? kpiCatalog : []) {
    for (const variant of base.selectionVariants || []) {
      const variantKey = normalizeKpiKey(
        variant.scoringWeightKey ||
          scoringSpecKpiKey(variant.displayName || variant.variantCode)
      );
      const variantAliases = expandSimulationKpiKeyAliases(variantKey);
      if (!variantAliases.some((alias) => strategyAliases.has(alias))) continue;

      for (const candidate of [
        variant.carrierKpiName,
        variant.variantCode,
        variant.displayName,
        variant.scoringWeightKey,
        base.kpiCode,
      ]) {
        register(candidate);
      }
    }
  }

  if (isStrategyKpiLaneScoped(kpi, kpiCatalog)) {
    const nameHint = normalizeKpiKey(kpi?.name);
    for (const base of Array.isArray(kpiCatalog) ? kpiCatalog : []) {
      if (!isLaneScopedBaseKpi(base)) continue;
      const baseKey = normalizeKpiKey(base.kpiCode);
      const baseStem = baseKey.replace(/^lane_level_/, "");
      if (
        strategyAliases.has(baseKey) ||
        [...strategyAliases].some((alias) => alias.includes(baseStem)) ||
        nameHint.includes(baseStem)
      ) {
        register(base.kpiCode);
      }
    }
  }

  return names;
}

const carrierKpiRowMatchesLookup = (row, lookupNames) => {
  if (!row || !lookupNames?.size) return false;
  const candidates = [
    normalizeCarrierKpiName(row.kpiName),
    normalizeKpiKey(row.kpiName),
    String(row.kpiName || "").trim().toLowerCase(),
  ].filter(Boolean);

  return candidates.some((candidate) => {
    if (lookupNames.has(candidate)) return true;
    return expandSimulationKpiKeyAliases(candidate).some((alias) => lookupNames.has(alias));
  });
};

function findCarrierKpiRowForStrategyKpi(
  carrierRows = [],
  lookupNames,
  { officeCode = "", simulationLaneKey = "", laneScoped = false } = {}
) {
  const matches = (carrierRows || []).filter((row) => carrierKpiRowMatchesLookup(row, lookupNames));
  return pickPreferredCarrierKpiRow(matches, {
    preferredOffice: officeCode,
    preferredLaneKey: simulationLaneKey,
    preferLaneScopedRow: laneScoped,
  });
}

const applySimulationRankedRowFallbacks = (carrier, strategyKpis = []) => {
  const extraScores = [];

  for (const kpi of Array.isArray(strategyKpis) ? strategyKpis : []) {
    const key = strategyKpiToKey(kpi);
    if (!key) continue;
    if (getSimulationKpiValue(carrier, key) != null) continue;

    if (isCostStrategyKpiKey(key)) {
      const quotedCost = toNumber(carrier?.price);
      if (quotedCost != null) {
        extraScores.push({
          key,
          label: (kpi?.name || labelForSimulationKpiKey(key)).trim(),
          value: quotedCost,
        });
      }
    }
  }

  if (!extraScores.length) return carrier;
  return {
    ...carrier,
    kpiScores: mergeSimulationKpiScores(carrier.kpiScores, extraScores),
  };
};

/** Attach carrier_kpi display values to ranked rows when lane-paths has no per-KPI breakdown. KPI factor stays from lane-paths API. */
export function enrichSimulationCarriersWithKpis(
  rankedRows = [],
  strategyKpis = [],
  carrierKpiRows = [],
  kpiCatalog = [],
  { officeCode = "", simulationLaneKey = "" } = {}
) {
  const catalogBridgedNames = collectCatalogBridgedCarrierKpiNames(kpiCatalog);

  const rowsByCarrier = new Map();
  for (const row of Array.isArray(carrierKpiRows) ? carrierKpiRows : []) {
    const carrierCode = String(row?.carrierCode || "").trim().toUpperCase();
    const kpiName = String(row?.kpiName || "").trim();
    if (!carrierCode || !kpiName) continue;
    if (!rowsByCarrier.has(carrierCode)) rowsByCarrier.set(carrierCode, []);
    rowsByCarrier.get(carrierCode).push(row);
  }

  return rankedRows.map((carrier) => {
    const carrierCode = String(carrier?.carrierCode || "").trim().toUpperCase();
    const carrierRows = rowsByCarrier.get(carrierCode) || [];
    const fromCarrierKpi = [];

    for (const kpi of Array.isArray(strategyKpis) ? strategyKpis : []) {
      const strategyKey = strategyKpiToKey(kpi);
      if (!strategyKey) continue;

      const lookupNames = collectStrategyKpiLookupNames(kpi, kpiCatalog);
      const laneScoped = isStrategyKpiLaneScoped(kpi, kpiCatalog);
      if (findSimulationKpiScore(carrier, strategyKey)) continue;

      const bestRow = findCarrierKpiRowForStrategyKpi(carrierRows, lookupNames, {
        officeCode,
        simulationLaneKey,
        laneScoped,
      });
      if (!bestRow || bestRow.kpiValue == null || bestRow.kpiValue === "") continue;

      const penalty = isPenaltyCarrierKpiName(bestRow.kpiName, catalogBridgedNames);
      const rawPenalty = toNumber(bestRow.kpiValue);
      const value = carrierKpiStoredValueToRate(bestRow.kpiValue, {
        isPenalty: penalty,
        kpiName: bestRow.kpiName,
      });
      if (value == null) continue;

      fromCarrierKpi.push({
        key: strategyKey,
        label: (kpi?.name || labelForSimulationKpiKey(strategyKey)).trim(),
        value,
        penaltyMultiplier: rawPenalty,
      });
    }

    const mergedScores = mergeSimulationKpiScores(carrier.kpiScores, fromCarrierKpi);
    const enriched = {
      ...carrier,
      kpiScores: mergedScores,
      kpiPenaltyByKey: buildSimulationPenaltyMapFromKpiScores(mergedScores),
    };
    return applySimulationRankedRowFallbacks(enriched, strategyKpis);
  });
}

const NEUTRAL_PENALTY_MULTIPLIER = 1;

const isExcludedSimulationScoringWeightKey = (key) => {
  const normalized = normalizeKpiKey(key);
  return normalized === "cost" || normalized.startsWith("base_cost");
};

const resolveSimulationPenaltyMultiplier = (weightKey, penaltyByWeightKey = {}) => {
  const map =
    penaltyByWeightKey instanceof Map
      ? penaltyByWeightKey
      : new Map(Object.entries(penaltyByWeightKey || {}));

  for (const alias of expandSimulationPenaltyLookupAliases(weightKey)) {
    if (map.has(alias)) {
      const value = toNumber(map.get(alias));
      if (value != null) return value;
    }
  }
  return NEUTRAL_PENALTY_MULTIPLIER;
};

/** KPI factor = Σ(strategy weight × penalty multiplier); mirrors backend CarrierKpiFactorCalculator. */
export function computeSimulationKpiFactor(strategyKpis = [], penaltyByWeightKey = {}) {
  let total = 0;
  let hasPositiveWeight = false;

  for (const kpi of strategyKpis || []) {
    const key = strategyKpiToKey(kpi);
    if (!key || isExcludedSimulationScoringWeightKey(key)) continue;
    const weight = (Number(kpi.percent) || 0) / 100;
    if (weight <= 0) continue;
    hasPositiveWeight = true;
    total += weight * resolveSimulationPenaltyMultiplier(key, penaltyByWeightKey);
  }

  if (!hasPositiveWeight) return null;
  if (total <= 0 || !Number.isFinite(total)) return NEUTRAL_PENALTY_MULTIPLIER;
  return total;
};

export function buildSimulationPenaltyMapFromKpiScores(kpiScores = []) {
  const map = new Map();
  for (const score of kpiScores || []) {
    const key = normalizeKpiKey(score.key);
    if (!key || isExcludedSimulationScoringWeightKey(key)) continue;
    const penalty = toNumber(score.penaltyMultiplier);
    if (penalty == null) continue;
    map.set(key, penalty);
    expandSimulationPenaltyLookupAliases(key).forEach((alias) => {
      if (!map.has(alias)) map.set(alias, penalty);
    });
  }
  return map;
}

const resolveSimulationPenaltyMap = (row) =>
  row?.kpiPenaltyByKey instanceof Map
    ? row.kpiPenaltyByKey
    : buildSimulationPenaltyMapFromKpiScores(row?.kpiScores);

const simulationPenaltyMapHasCarrierData = (row, strategyKpis = []) => {
  const penaltyMap = resolveSimulationPenaltyMap(row);
  if (!penaltyMap?.size) return false;
  for (const kpi of strategyKpis || []) {
    const key = strategyKpiToKey(kpi);
    if (!key || isExcludedSimulationScoringWeightKey(key)) continue;
    for (const alias of expandSimulationKpiKeyAliases(key)) {
      if (penaltyMap.has(alias)) return true;
    }
  }
  return false;
};

/**
 * Apply KPI factor + KPI total on ranked rows from lane-paths ranking (same backend as rate shopping).
 * Client-side carrier_kpi enrichment is display-only and must not override API kpi_factor.
 */
export function applySimulationKpiFactors(rankedRows = [], strategyKpis = []) {
  return rankedRows.map((row) => {
    const apiKpiFactor = toNumber(
      row?.sourceRow?.kpi_factor ??
        row?.sourceRow?.kpiFactor ??
        row?.sourceRow?.kpi_factor_detail?.kpi_factor ??
        row?.sourceRow?.kpiFactorDetail?.kpiFactor ??
        row?.kpiFactor
    );
    const penaltyByWeightKey =
      row.kpiPenaltyByKey instanceof Map
        ? row.kpiPenaltyByKey
        : buildSimulationPenaltyMapFromKpiScores(row.kpiScores);
    const computedFactor = computeSimulationKpiFactor(strategyKpis, penaltyByWeightKey);
    const kpiFactor = apiKpiFactor ?? computedFactor;

    const price = toNumber(row.price);
    const apiKpiCost = toNumber(
      row?.sourceRow?.price ?? row?.sourceRow?.kpi_cost ?? row?.sourceRow?.kpiCost
    );
    const cost =
      apiKpiFactor != null && apiKpiCost != null
        ? apiKpiCost
        : price != null && kpiFactor != null
          ? price * kpiFactor
          : toNumber(row.cost);

    return {
      ...row,
      kpiPenaltyByKey: penaltyByWeightKey,
      kpiFactor,
      cost,
    };
  });
}

const simulationRateToPenaltyMultiplier = (rate) => {
  const n = toNumber(rate);
  if (n == null || n <= 0) return null;
  if (n <= 1) return Math.min(3, 1 / n);
  return n;
};

const simulationKpiScoresToBreakdownRows = (kpiScores = [], strategyKpis = []) => {
  const weightByKey = new Map();
  for (const kpi of strategyKpis || []) {
    const key = strategyKpiToKey(kpi);
    if (!key) continue;
    weightByKey.set(key, (Number(kpi.percent) || 0) / 100);
  }

  return (kpiScores || []).map((score) => {
    const key = normalizeKpiKey(score.key);
    const aliases = expandSimulationKpiKeyAliases(key);
    const weight =
      weightByKey.get(key) ??
      aliases.map((alias) => weightByKey.get(alias)).find((value) => value != null);
    const rate = toNumber(score.value);
    const penaltyMultiplier =
      toNumber(score.penaltyMultiplier) ?? simulationRateToPenaltyMultiplier(rate);
    return {
      name: score.label || labelForSimulationKpiKey(key),
      score: rate,
      onTimeRate: rate,
      penaltyMultiplier,
      weight,
      usedInKpiFactor: weight != null && weight > 0,
    };
  });
};

const buildSimulationPriceCalculation = (basePrice, kpiFactor, rankCost) => {
  const cost = toNumber(basePrice);
  const factor = toNumber(kpiFactor);
  if (cost == null || factor == null) return null;
  const price = toNumber(rankCost) ?? cost * factor;
  return {
    formula: `price = cost × kpi_factor = ${cost.toFixed(4)} × ${factor.toFixed(4)} = ${price.toFixed(4)}`,
    cost,
    kpiFactor: factor,
    price,
  };
};

const SIMULATION_KPI_FACTOR_FORMULA =
  "kpi_factor = Σ(weight × penalty_multiplier); missing KPI defaults to 1.0; weights exclude cost";

const formatSimulationKpiFactorStepCalculation = (weight, penaltyMultiplier) => {
  const w = toNumber(weight);
  const p = toNumber(penaltyMultiplier);
  if (w == null || p == null) return null;
  return `${w.toFixed(4)} × ${p.toFixed(4)} = ${(w * p).toFixed(4)}`;
};

/** Strategy-weight KPI factor steps (same shape as rate-shopping score details). */
export function buildSimulationKpiFactorCalculation(row, strategyKpis = []) {
  const penaltyMap = resolveSimulationPenaltyMap(row);
  const computedFactor = computeSimulationKpiFactor(strategyKpis, penaltyMap);
  const apiFactor =
    toNumber(row?.sourceRow?.kpi_factor ?? row?.sourceRow?.kpiFactor) ??
    toNumber(row?.kpiFactor);
  const kpiFactor = apiFactor ?? computedFactor;

  const steps = (strategyKpis || [])
    .map((kpi) => {
      const key = strategyKpiToKey(kpi);
      if (!key || isExcludedSimulationScoringWeightKey(key)) return null;
      const weight = (Number(kpi.percent) || 0) / 100;
      if (weight <= 0) return null;

      const penaltyMultiplier = resolveSimulationPenaltyMultiplier(key, penaltyMap);
      const onTimeRate = getSimulationKpiValue(row, key);
      const contribution = weight * penaltyMultiplier;

      return {
        name: (kpi.name || labelForSimulationKpiKey(key)).trim(),
        weight,
        score: penaltyMultiplier,
        penaltyMultiplier,
        contribution,
        calculation: formatSimulationKpiFactorStepCalculation(weight, penaltyMultiplier),
        onTimeRate,
      };
    })
    .filter(Boolean);

  if (!steps.length) return null;
  return {
    formula: SIMULATION_KPI_FACTOR_FORMULA,
    kpiFactor,
    steps,
  };
};

const enrichSimulationKpiFactorSteps = (steps = []) =>
  (steps || []).map((step) => {
    const weight = toNumber(step.weight);
    const penalty = toNumber(step.score ?? step.penaltyMultiplier);
    const contribution =
      toNumber(step.contribution ?? step.weighted_contribution ?? step.weightedContribution) ??
      (weight != null && penalty != null ? weight * penalty : null);

    return {
      ...step,
      weight: weight ?? step.weight,
      score: penalty ?? step.score,
      penaltyMultiplier: penalty ?? step.penaltyMultiplier,
      contribution,
      calculation:
        step.calculation ??
        (weight != null && penalty != null
          ? formatSimulationKpiFactorStepCalculation(weight, penalty)
          : null),
    };
  });

const simulationKpiScoreRowsFromStrategy = (row, strategyKpis = []) => {
  const client = buildSimulationKpiFactorCalculation(row, strategyKpis);
  if (!client?.steps?.length) return [];
  return client.steps.map((step) => ({
    name: step.name,
    score: step.onTimeRate,
    penaltyMultiplier: step.penaltyMultiplier,
    onTimeRate: step.onTimeRate,
    weight: step.weight,
    usedInKpiFactor: true,
  }));
};

/** Build score-details popup payload from the same ranked-carrier fields as the simulation table. */
export function buildSimulationScoreBreakdown(row, strategyKpis = []) {
  const sourceRow = row?.sourceRow;
  const carrierCode = row?.carrierCode;
  const basePrice = toNumber(row?.price);
  const apiKpiFactor = toNumber(
    sourceRow?.kpi_factor ??
      sourceRow?.kpiFactor ??
      sourceRow?.kpi_factor_detail?.kpi_factor ??
      sourceRow?.kpiFactorDetail?.kpiFactor ??
      row?.kpiFactor
  );
  const apiRankCost = toNumber(sourceRow?.price ?? row?.cost);
  const displayKpiFactor = apiKpiFactor;
  const displayRankCost =
    apiRankCost ??
    (basePrice != null && displayKpiFactor != null ? basePrice * displayKpiFactor : null);
  const hasApiKpiDetail = Boolean(
    sourceRow?.kpi_factor_detail?.steps?.length || sourceRow?.kpiFactorDetail?.steps?.length
  );

  const summaryRows = [
    row?.rank != null ? { name: "Rank", value: row.rank } : null,
    basePrice != null ? { name: "Price", value: basePrice } : null,
    displayKpiFactor != null ? { name: "KPI factor", value: displayKpiFactor } : null,
    displayRankCost != null ? { name: "KPI-Cost", value: displayRankCost } : null,
  ].filter(Boolean);

  let kpiScoreRows = simulationKpiScoresToBreakdownRows(row?.kpiScores, strategyKpis);

  let kpiFactorCalculation = hasApiKpiDetail ? null : buildSimulationKpiFactorCalculation(row, strategyKpis);
  let priceCalculation = buildSimulationPriceCalculation(
    basePrice,
    displayKpiFactor,
    displayRankCost
  );
  let rankingFormula =
    "Rank priority = lowest RankCost (KPI-Cost = Price × KPI factor) first.";

  if (sourceRow) {
    const apiBreakdown = collectCarrierScoringBreakdown(
      { items: [sourceRow] },
      carrierCode,
      sourceRow
    );
    if (apiBreakdown.kpiScoreRows?.length) {
      kpiScoreRows = apiBreakdown.kpiScoreRows;
    }
    if (apiBreakdown.kpiFactorCalculation?.steps?.length) {
      kpiFactorCalculation = {
        ...apiBreakdown.kpiFactorCalculation,
        formula:
          apiBreakdown.kpiFactorCalculation.formula || SIMULATION_KPI_FACTOR_FORMULA,
        kpiFactor: displayKpiFactor ?? apiBreakdown.kpiFactorCalculation.kpiFactor,
        steps: enrichSimulationKpiFactorSteps(apiBreakdown.kpiFactorCalculation.steps),
      };
    }
    if (apiBreakdown.priceCalculation) {
      priceCalculation = {
        ...apiBreakdown.priceCalculation,
        cost: basePrice ?? apiBreakdown.priceCalculation.cost,
        kpiFactor: displayKpiFactor ?? apiBreakdown.priceCalculation.kpiFactor,
        price: displayRankCost ?? apiBreakdown.priceCalculation.price,
      };
    }
    if (apiBreakdown.rankingFormula) {
      rankingFormula = apiBreakdown.rankingFormula;
    }
  }

  if (!kpiScoreRows.length) {
    kpiScoreRows = simulationKpiScoreRowsFromStrategy(row, strategyKpis);
  }

  if (kpiFactorCalculation && displayKpiFactor != null) {
    kpiFactorCalculation = {
      ...kpiFactorCalculation,
      formula: kpiFactorCalculation.formula || SIMULATION_KPI_FACTOR_FORMULA,
      kpiFactor: displayKpiFactor,
      steps: enrichSimulationKpiFactorSteps(kpiFactorCalculation.steps || []),
    };
  }

  if (!priceCalculation && basePrice != null && displayKpiFactor != null) {
    priceCalculation = buildSimulationPriceCalculation(
      basePrice,
      displayKpiFactor,
      displayRankCost
    );
  }

  return {
    summaryRows,
    kpiScoreRows,
    kpiFactorCalculation,
    priceCalculation,
    rankingFormula,
  };
}

/** @deprecated Use buildSimulationScoreBreakdown. */
export function enhanceSimulationScoreBreakdown(_breakdown, row, strategyKpis = []) {
  return buildSimulationScoreBreakdown(row, strategyKpis);
}

const computeAllocationDeficit = (targetPct, actualPct) => {
  const target = toNumber(targetPct);
  const actual = toNumber(actualPct);
  if (target == null && actual == null) return null;
  return (target ?? 0) - (actual ?? 0);
};

/** RankCost for comparison — lower KPI-adjusted cost is better. */
export const carrierRankCost = (carrier) => {
  const cost = toNumber(carrier?.cost);
  if (cost != null) return cost;
  const price = toNumber(carrier?.price);
  const kpiFactor = toNumber(carrier?.kpiFactor ?? carrier?.score);
  if (price != null && kpiFactor != null) return price * kpiFactor;
  return price;
};

export const normalizeRankedCarrierRows = (rows = []) =>
  rows.map((row, index) => {
    const rank = Number(row?.rank ?? row?.carrier_rank ?? index + 1);
    const code = (row?.carrier_code ?? row?.carrierCode ?? row?.code ?? "").toString().trim();
    const name = (row?.carrier_name ?? row?.carrierName ?? row?.name ?? code).toString().trim();
    const kpiFactor = toNumber(row?.kpi_factor ?? row?.kpiFactor);
    // Match shipment rate shopping: cost = quoted lane total (BASE+FSC), price = KPI-adjusted total.
    const quotedTotal = toNumber(row?.cost);
    const kpiAdjustedTotal =
      toNumber(row?.price) ??
      (quotedTotal != null && kpiFactor != null ? quotedTotal * kpiFactor : null);

    return {
      rank: Number.isFinite(rank) ? rank : index + 1,
      carrierCode: code,
      carrierName: name || code || "Unknown carrier",
      kpiFactor,
      price: quotedTotal,
      cost: kpiAdjustedTotal,
      kpiScores: extractKpiScoresFromRankedCarrier(row),
      sourceRow: row,
      score: kpiFactor,
      targetAllocationPct: row?.target_allocation_pct ?? row?.targetAllocationPct ?? null,
      actualAllocationPct: row?.actual_allocation_pct ?? row?.actualAllocationPct ?? null,
      allocationDeficit:
        toNumber(row?.allocation_deficit ?? row?.allocationDeficit) ??
        computeAllocationDeficit(
          row?.target_allocation_pct ?? row?.targetAllocationPct,
          row?.actual_allocation_pct ?? row?.actualAllocationPct
        ),
      weightedFirstPick: row?.weighted_first_pick ?? row?.weightedFirstPick ?? null,
    };
  });

/** Deterministic carrier pick from ranked scores (no random roll). */
export function pickSimulationCarrier(carriers = []) {
  const list = [...carriers].sort((a, b) => a.rank - b.rank);
  if (!list.length) return { selected: null, method: null };

  const flagged = list.find((carrier) => carrier.weightedFirstPick === true);
  if (flagged) {
    return { selected: flagged, method: "weighted_first_pick" };
  }

  const withDeficit = list.filter((carrier) => carrier.allocationDeficit != null);
  if (withDeficit.length) {
    const maxDeficit = Math.max(...withDeficit.map((carrier) => carrier.allocationDeficit));
    if (maxDeficit > 0) {
      const selected = withDeficit
        .filter((carrier) => carrier.allocationDeficit === maxDeficit)
        .sort((a, b) => a.rank - b.rank)[0];
      return { selected, method: "allocation_deficit" };
    }
  }

  const selected = [...list].sort((a, b) => {
    const costA = carrierRankCost(a);
    const costB = carrierRankCost(b);
    if (costA != null && costB != null && costA !== costB) return costA - costB;
    if (costA != null && costB == null) return -1;
    if (costA == null && costB != null) return 1;
    return a.rank - b.rank;
  })[0];

  return { selected, method: "rankcost" };
}

export const normalizeAllocationWeights = (allocationWeights) => {
  const source =
    Array.isArray(allocationWeights) && allocationWeights.length
      ? allocationWeights
      : DEFAULT_ALLOCATION_WEIGHTS;
  return [...source]
    .map((w, index) => ({
      rank: Number(w.rank ?? index + 1),
      weightPct: Math.max(0, Number(w.weightPct ?? w.weight_pct ?? 0)),
      color: w.color,
    }))
    .sort((a, b) => a.rank - b.rank);
};

/**
 * Apply strategy allocation context and pick a carrier from ranked scores.
 * Selection is deterministic: allocation deficit, then best RankCost.
 */
export function runWeightedSelectionSimulation({
  allocationWeights,
  rankedCarriers = [],
  allocationMode = "WEIGHTED",
} = {}) {
  if (!rankedCarriers.length) {
    throw new Error("Ranked carriers are required to run simulation.");
  }

  normalizeAllocationWeights(allocationWeights);
  const carriers = [...rankedCarriers].sort((a, b) => a.rank - b.rank);

  const logs = [];
  const stamp = () => `[${formatLogTime()}]`;

  logs.push(`${stamp()} Starting selection...`);
  logs.push(
    `${stamp()} Available carriers: ${carriers
      .map((c) => {
        const rankCost = carrierRankCost(c);
        const rankCostLabel =
          rankCost != null ? `, RankCost ${rankCost.toFixed(2)}` : "";
        return `${c.carrierName}${c.carrierCode ? ` (${c.carrierCode})` : ""} — Rank ${c.rank}${rankCostLabel}`;
      })
      .join(", ")}`
  );
  logs.push(`${stamp()} Evaluating carrier scores...`);

  const { selected, method } = pickSimulationCarrier(carriers);
  const targetRank = selected?.rank ?? carriers[0]?.rank ?? 1;

  if (method === "weighted_first_pick") {
    logs.push(`${stamp()} Carrier flagged by allocation engine. Target Rank selected: ${targetRank}`);
  } else if (method === "allocation_deficit") {
    logs.push(
      `${stamp()} Highest allocation deficit: ${selected.allocationDeficit?.toFixed?.(2) ?? selected.allocationDeficit}. Target Rank selected: ${targetRank}`
    );
  } else {
    const rankCost = carrierRankCost(selected);
    logs.push(
      `${stamp()} Best RankCost score: ${rankCost != null ? rankCost.toFixed(2) : "—"}. Target Rank selected: ${targetRank}`
    );
  }

  if (selected) {
    logs.push(
      `${stamp()} Target carrier found: ${selected.carrierName}. Pre-selecting for waterfall.`
    );
  } else {
    logs.push(`${stamp()} No carrier could be selected from ranked results.`);
  }

  return {
    logs,
    selectionMethod: method,
    selectionRankCost: selected ? carrierRankCost(selected) : null,
    targetRank,
    selectedCarrier: selected,
    rankedCarriers: carriers,
    allocationMode: allocationMode || "WEIGHTED",
  };
}
